import React, { useState } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Paper,
  Typography,
  Alert
} from '@mui/material';
import QuestionsStep from './QuestionsStep';
import RulesStep from './RulesStep';
import TestStep from './TestStep';

interface RiskScopingWizardProps {
  tenantId?: number;
  onComplete?: () => void;
}

const steps = [
  { label: 'Questions', icon: '📋' },
  { label: 'Rules', icon: '⚖️' },
  { label: 'Review & Save', icon: '✅' }
];

const RiskScopingWizard: React.FC<RiskScopingWizardProps> = ({
  tenantId,
  onComplete
}) => {
  console.log('🏢 RiskScopingWizard received tenantId:', tenantId);

  const [activeStep, setActiveStep] = useState(0);
  const [questions, setQuestions] = useState<any[]>([]);
  const [rules, setRules] = useState<any[]>([]);
  const [testResults, setTestResults] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
    setError(null);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setError(null);
  };

  const handleReset = () => {
    setActiveStep(0);
    setQuestions([]);
    setRules([]);
    setTestResults(null);
    setError(null);
  };

  const handleQuestionsComplete = (updatedQuestions: any[]) => {
    setQuestions(updatedQuestions);
    handleNext();
  };

  const handleRulesComplete = (updatedRules: any[]) => {
    setRules(updatedRules);
    handleNext();
  };

  const handleTestComplete = (results: any) => {
    setTestResults(results);
    // Configuration is complete, call onComplete
    if (onComplete) {
      onComplete();
    }
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <QuestionsStep
            tenantId={tenantId}
            onNext={handleQuestionsComplete}
            onBack={handleBack}
            initialQuestions={questions}
          />
        );
      case 1:
        return (
          <RulesStep
            tenantId={tenantId}
            questions={questions}
            onNext={handleRulesComplete}
            onBack={handleBack}
            initialRules={rules}
          />
        );
      case 2:
        return (
          <TestStep
            tenantId={tenantId}
            questions={questions}
            rules={rules}
            onNext={handleTestComplete}
            onBack={handleBack}
          />
        );
      default:
        return <Typography>Unknown step</Typography>;
    }
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Risk Scoping Configuration Wizard
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure intelligent risk filtering for your organization
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{step.icon}</span>
                  <span>{step.label}</span>
                </Box>
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ minHeight: 400 }}>
          {getStepContent(activeStep)}
        </Box>
      </Paper>
    </Box>
  );
};

export default RiskScopingWizard;
